import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { DEFER_WELCOME_QUERY, isOnboardingAllowedPath } from '../constants/onboarding';
import { useNeedsOnboarding } from '../hooks/useNeedsOnboarding';
import { useWaiting, WAITLIST_URL } from '../pages/welcome/useWaiting';

export function OnboardingRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { needsOnboarding } = useNeedsOnboarding();
  // A member held on the waitlist hears "you're on the list" first, on the waitlist page; its
  // "Build my profile" card is their way into the waiting wizard.
  const { waiting, loading: accessLoading } = useWaiting({ skip: !needsOnboarding });

  useEffect(() => {
    if (!needsOnboarding || accessLoading) return;

    // Both of these match at a segment boundary and ignore case, because that is
    // how react-router matches the routes themselves — same reason
    // `isOnboardingAllowedPath` lowercases. A bare `startsWith('/welcome')` read
    // `/welcome-anything` as the wizard, and since no such route exists it renders
    // `NotFound` outside `AppLayout`, where the resume banner cannot reach: the
    // user is stranded on a 404 with the bounce suppressed. A bare
    // `startsWith('/art')` both unlocked `/art-marketing-autopilot` via
    // `?defer_welcome=1` and missed `/Art/123`, dropping a deep link the query
    // param exists to preserve.
    if (/^\/welcome(\/|$)/i.test(location.pathname)) return;

    if (waiting) {
      window.location.assign(WAITLIST_URL());
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const deferRaw = searchParams.get(DEFER_WELCOME_QUERY);
    const onArtPath = /^\/art(\/|$)/i.test(location.pathname);
    const deferWelcome = onArtPath && (deferRaw === '1' || deferRaw === 'true');
    if (deferWelcome) return;

    // Account management stays reachable mid-wizard: a user who wants to change
    // plan or cancel must not be bounced back to /welcome.
    if (isOnboardingAllowedPath(location.pathname)) return;

    // Replace, or Back re-renders the bounced-from route, which pushes /welcome
    // again — a dead Back button and one history entry per press.
    navigate('/welcome', { replace: true });
  }, [needsOnboarding, accessLoading, waiting, location.pathname, location.search, navigate]);

  return null;
}
